import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
} from "@jhool-io/fe-components";
import useToast from "../../../../hooks/useToast";
import { useFetchClientTags } from "../../hooks/clients.queries";
import {
    useAddClientTag,
    useRemoveClientTag,
} from "../../hooks/clients.mutations";
import {
    CLIENT_TAG_DETAILS,
    CLIENT_TAG_KEYS,
} from "../../helpers/clients.helpers";
import { ClientTagKey, ClientTagKind } from "../../types/clients.types";
import ClientTagsHistory from "../ClientTagsHistory/ClientTagsHistory";

interface EditClientTagsMenuProps {
    clientId: string;
    children: React.ReactElement;
}

export default function EditClientTagsMenu({
    clientId,
    children,
}: EditClientTagsMenuProps) {
    const [isHistoryOpen, setIsHistoryOpen] = React.useState(false);

    const clientTags = useFetchClientTags(clientId, Boolean(clientId));
    const addClientTag = useAddClientTag(clientId);
    const removeClientTag = useRemoveClientTag(clientId);

    const queryClient = useQueryClient();

    const { toast } = useToast();

    const activeTags = clientTags.data?.data || [];
    const selectedTagKeys = activeTags.map((tag) => tag.tag_key);
    const automaticTagKeys = activeTags
        .filter((tag) => tag.kind === ClientTagKind.AUTOMATIC)
        .map((tag) => tag.tag_key);
    const isUpdating = addClientTag.isLoading || removeClientTag.isLoading;

    const handleTagClick = (tagKey: ClientTagKey) => {
        const mutation = selectedTagKeys.includes(tagKey)
            ? removeClientTag
            : addClientTag;
        mutation.mutate(tagKey, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [clientId, "client-tags"],
                });
                await queryClient.invalidateQueries({
                    queryKey: [clientId, "client-tag-activities"],
                });
                toast({
                    mode: "success",
                    message: res.message || "Client tags updated",
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not update client tags at this time.",
                });
            },
        });
    };

    return (
        <>
            <Dialog open={isHistoryOpen}>
                <DialogContent
                    title="Tag history"
                    variant="center"
                    handleCloseDialog={() => setIsHistoryOpen(false)}
                    showFooter={false}
                    wrapperClass="w-[850px]"
                >
                    <ClientTagsHistory clientId={clientId} />
                </DialogContent>
            </Dialog>
            <Dropdown>
                <DropdownTrigger asChild>{children}</DropdownTrigger>
                <DropdownContent
                    align="end"
                    width={320}
                    maxHeight={420}
                    className="overflow-y-auto"
                >
                    {CLIENT_TAG_KEYS.map((tagKey) => (
                        <DropdownItem
                            key={tagKey}
                            onClick={() => handleTagClick(tagKey)}
                            isMulti
                            itemValue={tagKey}
                            values={selectedTagKeys}
                            disabled={
                                isUpdating || automaticTagKeys.includes(tagKey)
                            }
                        >
                            <span className="flex flex-col normal-case py-1">
                                <span>{CLIENT_TAG_DETAILS[tagKey].label}</span>
                                <span className="text-xs font-normal text-textSec">
                                    {CLIENT_TAG_DETAILS[tagKey].description}
                                </span>
                            </span>
                        </DropdownItem>
                    ))}
                    <div className="my-1 border-t border-stroke" />
                    <DropdownItem onClick={() => setIsHistoryOpen(true)}>
                        <span className="normal-case text-primary">
                            View tag history
                        </span>
                    </DropdownItem>
                </DropdownContent>
            </Dropdown>
        </>
    );
}
